import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import style from '../style/dealingwithresistancedetails.scss'

import AddOnIcon from '../../AddOnIcon/AddOnIcon';
import ScoreBarNG from '../../ScoreBarNG/ScoreBarNG';
import ScoreBarTypes from '../../../constants/ScoreBarTypes';
import ScoreRange from '../../../constants/ScoreRange';

/**
 * Renders the DealingWithResistanceDetailsModal component.
 * @param {Object} props - The component props.
 * @param {Object} props.i18nParticipation - Translations
 * @param {String} props.initials - The participant's initials.
 * @param {String} props.languageId - The user language ID.
 * @param {Function} props.onClose - The function to close the modal.
 * @param {Number} props.scoreIndex - Determines open/closed state of modal and content to display.
 * @param {Object} props.section - The section data.
 * @returns {JSX.Element} The DealingWithResistanceDetailsModal component.
 */
export const DealingWithResistanceDetailsModal = (props) => {
    const {
        changeScoreIndex,
        i18nParticipation,
        initials,
        languageId,
        onClose,
        section,
        scoreIndex,
    } = props;

    const modalRef = useRef(null);
    const currentScore = section.scores[scoreIndex];

    useEffect(() => {
        const { current: modalElement } = modalRef;
        if (modalElement && !modalElement.open && scoreIndex !== null) {
            modalElement.showModal();
        } else if (scoreIndex === null) {
            modalElement?.close();
        }
    }, [scoreIndex])

    const getBannerColor = () => {
        if (!currentScore) return;
        const { ranges, score } = currentScore;
        return `banner_${ranges.find(range => range.min <= score && range.max >= score).color}`;
    }

    // close if current equals target (if click is on the exact element)
    const handleCloseModal = (event) => {
        if (modalRef.current && event.target === modalRef.current) {
            event.stopPropagation();
            event.preventDefault();
            onClose();
        }
    };

    return (
        <dialog
            className={ style.modal_dialog }
            data-testid='dealingWithResistanceDetailsModal'
            id='dealingWithResistanceDetailsModal'
            onCancel={ onClose }
            onClick={handleCloseModal}
            onClose={ onClose }
            ref={ modalRef }
        >
            { scoreIndex !== null && (
                <div className={ style.modal_content_wrapper }>
                    <div className={ style.modal_header }>
                        <AddOnIcon handle={ `icon_addon_${currentScore.handle}` } />
                        <div className={ style.modal_header__text_wrapper }>
                            <p className={ style.modal_subheader }>{ currentScore.basedOn }</p>
                            <h3>{ currentScore.name }</h3>
                        </div>
                        <button
                            aria-label="close modal"
                            className={ style.close_button }
                            onClick={ (event) => {
                                event.stopPropagation();
                                onClose();
                            } }
                            type="button"
                        />
                    </div>
                    <span className={ `${style.modal_banner} ${style[getBannerColor()]}` }>
                        {currentScore.evaluation}
                    </span>
                    <div className={ style.modal_body }>
                        <div className={ style.score_bar_wrapper }>
                            <p className={ style.p_start }>{ currentScore.lowLabel }</p>
                            <ScoreBarNG
                                classes={ {
                                    colors: {
                                        red: style.red,
                                        orange: style.orange,
                                        green: style.green
                                    },
                                    root: style.score_bar
                                } }
                                scoreMin={ ScoreRange.DEALING_WITH_RESISTANCE.MIN }
                                scoreMax={ ScoreRange.DEALING_WITH_RESISTANCE.MAX }
                                scoreValue={ currentScore.score }
                                scoreName={ currentScore.name }
                                scoreBarType={ ScoreBarTypes.GAUGE }
                                fill={ false }
                                initials={ initials }
                                languageId={ languageId }
                                levelRanges={ currentScore.ranges }
                            />
                            <p className={ style.p_end }>{ currentScore.highLabel }</p>
                        </div>
                        <p className={ style.score_definition }>{ currentScore.definition }</p>
                    </div>
                    <div className={ style.modal_footer }>
                        <button
                            aria-label="show previous score"
                            className={ `action_button ${style.modal_button}` }
                            disabled={ scoreIndex === 0 }
                            onClick={ () => changeScoreIndex(scoreIndex - 1) }
                            type="button"
                        >
                            {i18nParticipation.report_derailment_back}
                        </button>
                        <button
                            aria-label='show next score'
                            className={ `action_button ${style.modal_button}` }
                            data-testid='nextButton'
                            disabled={ scoreIndex === section.scores.length - 1 }
                            onClick={ () => changeScoreIndex(scoreIndex + 1) }
                            type="button"
                        >
                            {i18nParticipation.report_derailment_next}
                        </button>
                    </div>
                </div>
            ) }
        </dialog >
    )
}

DealingWithResistanceDetailsModal.propTypes = {
    changeScoreIndex: PropTypes.func.isRequired,
    i18nParticipation: PropTypes.object.isRequired,
    initials: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    section: PropTypes.object.isRequired,
    scoreIndex: PropTypes.number,
    languageId: PropTypes.string,
}
